import { jwtDecode } from 'jwt-decode'

import { adamoApi } from '../../api/adamo-api'
import { logInfo, logWarn } from '../utils/sso-logger'

let cachedSuiteAccessToken: string | null = null
let cachedSuiteAccessTokenExpiresAt = 0

type RefreshTokenResponse = {
  token?: string
  accessToken?: string
  data?: {
    token?: string
    accessToken?: string
  }
}

function extractTokenFromRefreshResponse(body: RefreshTokenResponse | undefined): string {
  if (!body) {
    return ''
  }

  return (
    body.data?.token ??
    body.data?.accessToken ??
    body.token ??
    body.accessToken ??
    ''
  )
}

function getJwtExpiryMs(token: string): number {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token)

    if (!exp) {
      return Date.now() + 5 * 60 * 1000
    }

    // Refresh one minute before JWT exp (Pay legacy access tokens can be as short as 1m).
    return exp * 1000 - 60_000
  } catch {
    return Date.now() + 5 * 60 * 1000
  }
}

function isCachedTokenValid(now: number): boolean {
  if (!cachedSuiteAccessToken || cachedSuiteAccessTokenExpiresAt <= now) {
    return false
  }

  return getJwtExpiryMs(cachedSuiteAccessToken) > now
}

type GetSuiteAccessTokenOptions = {
  forceRefresh?: boolean
}

/**
 * Obtains a fresh suite access token via identity refresh (cookies).
 * Pay microservices on API Gateway / localhost cannot read httpOnly suite cookies directly.
 */
export async function getSuiteAccessTokenFromIdentity(
  options: GetSuiteAccessTokenOptions = {}
): Promise<string> {
  const now = Date.now()

  if (!options.forceRefresh && isCachedTokenValid(now)) {
    return cachedSuiteAccessToken!
  }

  if (options.forceRefresh) {
    clearSuiteAccessTokenCache()
  }

  logInfo('getSuiteAccessTokenFromIdentity — POST /api/v1/auth/refresh', {
    forceRefresh: Boolean(options.forceRefresh)
  })

  const response = await adamoApi.post<RefreshTokenResponse>('/api/v1/auth/refresh', {}, { withCredentials: true })

  const token = extractTokenFromRefreshResponse(response.data)

  if (!token) {
    throw new Error('Suite access token missing from identity refresh response')
  }

  cachedSuiteAccessToken = token
  cachedSuiteAccessTokenExpiresAt = getJwtExpiryMs(token)

  logInfo('getSuiteAccessTokenFromIdentity — suite access token resolved', {
    length: token.length,
    cacheUntil: new Date(cachedSuiteAccessTokenExpiresAt).toISOString()
  })

  if (cachedSuiteAccessTokenExpiresAt <= now) {
    logWarn('getSuiteAccessTokenFromIdentity — received JWT already expired or near expiry')
  }

  return token
}

export function clearSuiteAccessTokenCache(): void {
  cachedSuiteAccessToken = null
  cachedSuiteAccessTokenExpiresAt = 0
}
